import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './EventConfigurator.css';
import { db } from '../firebaseConfig';
import { collection, getDocs, addDoc } from 'firebase/firestore';

// Import the new modal component
import ServiceDescriptionModal from './ServiceDescriptionModal';

const termsAndConditionsText = `
  <h3>Términos y Condiciones para la Reserva de Salones</h3>
  <p><strong>1. Objeto del Contrato:</strong> Detalle claro del evento a realizar (tipo, fecha, hora, lugar, servicios incluidos).</p>
  <p><strong>2. Reservas y Confirmación:</strong> Las reservas están sujetas a disponibilidad y las tarifas pueden variar hasta que se confirmen. La reserva se considera confirmada con el pago de un anticipo.</p>
  <p><strong>3. Pagos:</strong> Se requiere un anticipo del 50% para confirmar la reserva. El pago final debe realizarse 10 días antes del evento.</p>
  <p><strong>4. Política de Cancelación:</strong> Las cancelaciones deben realizarse con al menos 30 días de antelación para un reembolso completo del anticipo. Las cancelaciones con menos de 30 días de antelación no son reembolsables.</p>
  <p><strong>5. Modificaciones:</strong> Los cambios en la reserva están sujetos a disponibilidad y pueden generar cargos adicionales.</p>
  <p><strong>6. Responsabilidades:</strong> El cliente es responsable por el comportamiento de los participantes y por cualquier daño a la propiedad.</p>
  <p><strong>7. Fuerza Mayor:</strong> Ninguna de las partes será responsable por incumplimiento debido a eventos imprevisibles e inevitables (desastres naturales, pandemias, etc.).</p>
  <p><strong>8. Aceptación de los Términos:</strong> Al realizar la reserva, el cliente declara que ha leído, entendido y aceptado estos términos y condiciones.</p>
`;

// This is a simplified card for the new multi-scene flow.
const SalonCard = ({ salon, onSelect }) => (
  <div className="salon-card" onClick={() => onSelect(salon)}>
    <img src={salon.imageUrl} alt={salon.title} />
    <div className="salon-card-name">{salon.title}</div>
  </div>
);

const ServiceCard = ({ service, onSelect, isSelected, onOpenDescriptionModal }) => {
  const [isExpanded, setIsExpanded] = useState(false); // New state for card expansion

  const handleCardClick = () => {
    if (window.innerWidth > 768) { // Only expand on desktop
      setIsExpanded(!isExpanded);
    } else {
      onSelect(service); // Original behavior for mobile (select the service)
    }
  };

  const handleSelectClick = (e) => {
    e.stopPropagation(); // Prevent card from collapsing
    onSelect(service);
  };

  const handleViewMoreClick = (e) => {
    e.stopPropagation(); // Prevent card from collapsing
    onOpenDescriptionModal(service); // Open modal with service description
  };

  const isMobile = window.innerWidth <= 768;

  return (
    <div className={`service-card ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`} onClick={handleCardClick} style={!isMobile ? { backgroundImage: `url(${service.imageUrl})` } : {}}>
      {isMobile && <img src={service.imageUrl} alt={service.name} className="service-card-image-mobile" />}
      <div className="service-card-overlay">
        <h4 className="service-card-name">{service.name}</h4>
        {isExpanded && window.innerWidth > 768 && ( // Show buttons only when expanded on desktop
          <div className="service-card-actions">
            <button onClick={handleViewMoreClick} className="view-more-button">Ver más</button>
            <button onClick={handleSelectClick} className="select-button">Seleccionar</button>
          </div>
        )}
      </div>
    </div>
  );
};

function EventConfigurator({ id }) {
  const [scene, setScene] = useState(1);
  const [sedes, setSedes] = useState([]);
  const [salones, setSalones] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedSede, setSelectedSede] = useState(null);
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [comments, setComments] = useState("");
  const [userName, setUserName] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [userEmail, setUserEmail] = useState(""); // New state for user email
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [initialBackgroundImage, setInitialBackgroundImage] = useState('/fondoexperiencias.png'); // Default fallback
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [modalService, setModalService] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenDescriptionModal = (service) => {
    setModalService(service);
    setShowDescriptionModal(true);
  };

  const handleCloseDescriptionModal = () => {
    setModalService(null);
    setShowDescriptionModal(false);
  };

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        // Fetch all data concurrently for efficiency
        const [spacesSnapshot, servicesSnapshot] = await Promise.all([
          getDocs(collection(db, 'eventSpaces')),
          getDocs(collection(db, 'services')),
        ]);

        const allSpaces = spacesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fetchedSedes = allSpaces.filter(space => space.type === 'sede');
        const fetchedSalones = allSpaces.filter(space => space.type === 'eventSpace');

        const fetchedServices = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        setSedes(fetchedSedes);
        setSalones(fetchedSalones);
        setServices(fetchedServices);

        if (fetchedSedes.length > 0) {
          setInitialBackgroundImage(fetchedSedes[0].imageUrl);

          // Check for sede ID from URL
          const params = new URLSearchParams(location.search);
          const sedeIdFromUrl = params.get('sede');
          if (sedeIdFromUrl) {
            const preSelectedSede = fetchedSedes.find(s => s.id === sedeIdFromUrl);
            if (preSelectedSede) {
              setSelectedSede(preSelectedSede);
              setScene(2);
            }
          }
        }

      } catch (err) {
        setError('Failed to load event data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [location]);

  const handleSedeSelect = (sede) => {
    setSelectedSede(sede);
    setScene(2);
  };

  const handleSalonSelect = (salon) => {
    setSelectedSalon(salon);
    setScene(3);
  };

  const handleServiceSelect = (service) => {
    setSelectedServices(prev =>
      prev.find(s => s.id === service.id)
        ? prev.filter(s => s.id !== service.id)
        : [...prev, service]
    );
  };

  const handleBooking = async () => {
    if (!termsAccepted) {
      alert("Debes aceptar los términos y condiciones para continuar.");
      return;
    }

    try {
      await addDoc(collection(db, "eventBookings"), {
        sedeId: selectedSede.id,
        sedeTitle: selectedSede.title,
        salonId: selectedSalon.id,
        salonTitle: selectedSalon.title,
        services: selectedServices.map(s => ({ id: s.id, name: s.name })),
        comments: comments,
        userName: userName,
        userPhone: userPhone,
        userEmail: userEmail, // Add userEmail
        createdAt: new Date(),
        status: "pendiente"
      });
      alert("¡Tu solicitud de cotización ha sido creada exitosamente!");
      // Reset state and navigate to home or a confirmation page
      setScene(1);
      setSelectedSede(null);
      setSelectedSalon(null);
      setSelectedServices([]);
      setComments("");
      setUserName("");
      setUserPhone("");
      setUserEmail(""); // Reset userEmail
      setTermsAccepted(false);
      navigate('/'); // Navigate to home after booking
    } catch (error) {
      console.error("Error creating booking: ", error);
      alert("Hubo un error al crear tu solicitud. Por favor, inténtalo de nuevo.");
    }
  };

  const renderScene1 = () => (
    <div className="scene-container">
      <div className="scene-content-wrapper">
        <h2 className="scene-title">Nuestras Sedes</h2>
        <p className="scene-subtitle">
          <span className="text-background">Elige una sede para ver los salones disponibles</span>
        </p>
        <div className="salon-selection-grid">
          {sedes.map(sede => (
            <SalonCard key={sede.id} salon={sede} onSelect={handleSedeSelect} />
          ))}
        </div>
      </div>
    </div>
  );

  const renderScene2 = () => {
    const filteredSalones = salones.filter(salon => salon.sedeId === selectedSede.id);
    return (
      <div className="scene-container">
        <div className="scene-content-wrapper">
          <h2 className="scene-title">Salones en {selectedSede.title}</h2>
          <p className="scene-subtitle">
            <span className="text-background">Estos son los salones disponibles en nuestra sede {selectedSede.title}</span>
          </p>
          <div className="salon-selection-grid">
            {filteredSalones.length > 0 ? (
              filteredSalones.map(salon => (
                <SalonCard key={salon.id} salon={salon} onSelect={handleSalonSelect} />
              ))
            ) : (
              <p>No hay salones disponibles para esta sede.</p>
            )}
          </div>
        </div>
        <div className="navigation-buttons">
          <button className="back-button" onClick={() => {setSelectedSede(null); setScene(1);}}>Volver a Sedes</button>
        </div>
      </div>
    );
  };

  const renderScene3 = () => (
    <div className="scene-container">
      <div className="scene-content-wrapper">
        <h2 className="scene-title">Servicios Adicionales</h2>
        <p className="scene-subtitle">
          <span className="text-background">Selecciona los servicios que deseas agregar a tu salón</span>
        </p>
        <div className="service-selection-grid">
          {services.map(service => (
            <ServiceCard
              key={service.id}
              service={service}
              onSelect={handleServiceSelect}
              isSelected={selectedServices.some(s => s.id === service.id)}
              onOpenDescriptionModal={handleOpenDescriptionModal} // Pass the new prop
            />
          ))}
        </div>
      </div>
      <div className="navigation-buttons">
        <button className="back-button" onClick={() => setScene(2)}>Volver a Salones</button>
        <button className="next-button" onClick={() => setScene(4)}>Siguiente</button>
      </div>
    </div>
  );

  const renderScene4 = () => (
    <div className="scene-container">
      <div className="scene-content-wrapper">
        <h2 className="scene-title">Información de Contacto y Comentarios</h2>
        <p className="scene-subtitle">
          <span className="text-background">Agrega cualquier comentario o solicitud especial</span>
        </p>
        <input
          type="text"
          className="contact-input"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          placeholder="Tu Nombre"
          style={{ marginBottom: '10px' }}
        />
        <input
          type="tel"
          className="contact-input"
          value={userPhone}
          onChange={(e) => setUserPhone(e.target.value)}
          placeholder="Tu Teléfono"
          style={{ marginBottom: '10px' }}
        />
        <input
          type="email"
          className="contact-input"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          placeholder="Tu Correo Electrónico"
          style={{ marginBottom: '10px' }}
        />
        <textarea
          className="comments-textarea"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Escribe tus comentarios aquí..."
        />
      </div>
      <div className="navigation-buttons">
        <button className="back-button" onClick={() => setScene(3)}>Volver a Servicios</button>
        <button className="next-button" onClick={() => setScene(5)}>Siguiente</button>
      </div>
    </div>
  );

  const renderScene5 = () => (
    <div className="scene-container">
      <div className="scene-content-wrapper">
        <h2 className="scene-title">Resumen de tu Solicitud</h2>
        <div className="summary-content">
          <p><strong>Sede:</strong> {selectedSede?.title}</p>
          <p><strong>Salón:</strong> {selectedSalon?.title}</p>
          <p><strong>Servicios:</strong></p>
          <ul>
            {selectedServices.map(s => <li key={s.id}>{s.name}</li>)}
          </ul>
          <p><strong>Comentarios:</strong> {comments}</p>
          <p><strong>Nombre de Contacto:</strong> {userName}</p>
          <p><strong>Teléfono de Contacto:</strong> {userPhone}</p>
          <p><strong>Correo Electrónico:</strong> {userEmail}</p>
        </div>
        <div className="terms-container" dangerouslySetInnerHTML={{ __html: termsAndConditionsText }} />
        <div className="terms-and-conditions">
          <input
            type="checkbox"
            id="terms"
            checked={termsAccepted}
            onChange={() => setTermsAccepted(!termsAccepted)}
          />
          <label htmlFor="terms">Acepto los términos y condiciones</label>
        </div>
      </div>
      <div className="navigation-buttons">
        <button className="back-button" onClick={() => setScene(4)}>Volver</button>
        <button className="book-button" onClick={handleBooking} disabled={!termsAccepted}>Solicitar Cotización</button>
      </div>
    </div>
  );

  const eventConfiguratorStyle = {
    backgroundImage: `url(${selectedSede ? selectedSede.imageUrl : initialBackgroundImage})`,
    backgroundColor: '#1a1a1a',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '50px 20px',
    color: 'white',
    textAlign: 'center'
  };

  if (loading) return <section id={id} style={eventConfiguratorStyle}><p>Loading salons...</p></section>;
  if (error) return <section id={id} style={eventConfiguratorStyle}><p>Error: {error}</p></section>;

  return (
    <section id={id} className="salon-configurator-container" style={eventConfiguratorStyle}>
      {scene === 1 && renderScene1()}
      {scene === 2 && renderScene2()}
      {scene === 3 && renderScene3()}
      {scene === 4 && renderScene4()}
      {scene === 5 && renderScene5()}

      {showDescriptionModal && modalService && (
        <ServiceDescriptionModal
          service={modalService}
          onClose={handleCloseDescriptionModal}
        />
      )}
    </section>
  );
}

export default EventConfigurator;
